import { useEffect, useSyncExternalStore } from "react";

/**
 * Maps Network Information API effective connection type to image quality.
 * Starts at the lowest usable quality and upgrades based on real connection speed.
 */

type ConnectionType = "slow-2g" | "2g" | "3g" | "4g";

const QUALITY_MAP: Record<ConnectionType, number> = {
  "slow-2g": 25,
  "2g": 30,
  "3g": 45,
  "4g": 75,
};

const DEFAULT_QUALITY = 25; // SSR & first paint — always start low
const FALLBACK_QUALITY = 65; // API not supported — assume decent connection

function getConnection() {
  if (typeof navigator === "undefined") return null;
  return (navigator as unknown as { connection?: { effectiveType: ConnectionType } })
    .connection ?? null;
}

function getQuality(): number {
  const conn = getConnection();
  if (!conn) return typeof window === "undefined" ? DEFAULT_QUALITY : FALLBACK_QUALITY;
  return QUALITY_MAP[conn.effectiveType] ?? FALLBACK_QUALITY;
}

let listeners: Array<() => void> = [];

function subscribe(cb: () => void) {
  listeners.push(cb);
  const conn = getConnection();
  if (conn) {
    (conn as unknown as EventTarget).addEventListener("change", cb);
  }
  return () => {
    listeners = listeners.filter((l) => l !== cb);
    const c = getConnection();
    if (c) {
      (c as unknown as EventTarget).removeEventListener("change", cb);
    }
  };
}

function getServerSnapshot() {
  return DEFAULT_QUALITY;
}

/**
 * Returns an image quality value (25–75) based on the user's current
 * network speed. Updates automatically when connection changes.
 *
 * SSR always returns 25 (lowest) so the initial HTML is 3G-safe.
 */
export function useNetworkQuality(): number {
  return useSyncExternalStore(subscribe, getQuality, getServerSnapshot);
}
